import { CrudField } from "../CrudField";
import { CrudProperty } from "../CrudProperty";

const DynamicFilterOperators = {
  equals: "is exactly",
  notEquals: "is not",
  contains: "contains",
  notContains: "doesn't contain",
  greaterThan: "is greater than",
  greaterThanOrEqual: "is at least",
  lessThan: "is less than",
  lessThanOrEqual: "is at most",
  isNull: "is blank",
  isNotNull: "is not blank",
  in: "is one of",
  notIn: "is none of",
};

export type DynamicFilterOperator = keyof typeof DynamicFilterOperators;

// Operators with no comparison value -- serializedValue/isSet special-case
// these the same way isNull always has.
export const VALUELESS_OPERATORS: DynamicFilterOperator[] = [
  "isNull",
  "isNotNull",
];

// Operators whose value is a SET of values rather than a single one -- the
// value widget renders as a multi-select for these, and switching to/from
// one of these must not strand an incompatible value type (array vs scalar)
// in the property.
export const SET_OPERATORS: DynamicFilterOperator[] = ["in", "notIn"];

export type DynamicFilterGroupMode = "any" | "none";

export interface DynamicFilterOpts {
  field: CrudField;
  id: number;
  serializedName?: string;
  operator?: DynamicFilterOperator;
  defaultOperator?: DynamicFilterOperator;
  value?: any;
  label?: string;
  groupKey?: string;
  groupMode?: DynamicFilterGroupMode;
}

export class DynamicFilter {
  public id: number;
  public field: CrudField;
  public property: CrudProperty;
  public operator: DynamicFilterOperator;
  public operatorLabelsMap: object = {};
  public disabledOperators: DynamicFilterOperator[] = [];
  public defaultOperator: DynamicFilterOperator;
  // Entries sharing a groupKey correlate on the same related row (see
  // DynamicFilterGroup server-side). Omitted from the wire format when unset
  // so a plain filter's payload is byte-identical to today's.
  public groupKey?: string;
  public groupMode?: DynamicFilterGroupMode;

  constructor(opts: DynamicFilterOpts) {
    this.id = opts.id;

    this.field = opts.field.clone({
      isOrphan: true,
      disableRules: ["required"],
    });

    this.property = this.field.property;

    if (typeof opts.value !== "undefined") {
      this.property.set(opts.value);
    }

    if (typeof opts.defaultOperator !== "undefined") {
      this.defaultOperator = opts.defaultOperator;
    } else {
      this.defaultOperator = this.operators[0].key as DynamicFilterOperator;
    }

    if (typeof opts.serializedName !== "undefined") {
      this._serializedName = opts.serializedName;
    }

    if (typeof opts.label !== "undefined") {
      this._label = opts.label;
    }

    if (typeof opts.operator !== "undefined") {
      this.operator = opts.operator;
    } else {
      this.operator = this.defaultOperator;
    }

    // Always assigned (even when undefined) so the property exists at
    // construction time -- Vue 2 only makes properties reactive that exist
    // when it first walks the object, so createGroup()/ungroup() setting
    // filter.groupKey later on a filter that never had one would otherwise
    // be a silent, non-reactive mutation (Vue's "can't detect new
    // property" caveat) and the grouped-block UI would never re-render.
    this.groupKey = opts.groupKey;
    this.groupMode = opts.groupMode;
  }

  public get serializedValue() {
    if (
      !this.operator ||
      (!VALUELESS_OPERATORS.includes(this.operator) && this.property.isEmpty)
    )
      return undefined;

    return {
      p: this.serializedName,
      o: this.operator,
      v: this.property.serializedValue,
      ...(this.groupKey ? { g: this.groupKey } : {}),
      ...(this.groupKey && this.groupMode ? { m: this.groupMode } : {}),
    };
  }

  private _serializedName?: string;
  public get serializedName() {
    return this._serializedName
      ? this._serializedName
      : this.property.serializedName;
  }

  public get operatorsMap() {
    return {
      ...DynamicFilterOperators,
      ...this.operatorLabelsMap,
    };
  }

  public get operators() {
    return Object.keys(this.operatorsMap)
      .filter(
        (key: string) =>
          !this.disabledOperators.includes(key as DynamicFilterOperator)
      )
      .map((key: string) => ({
        key,
        label: this.operatorsMap[key as DynamicFilterOperator],
      }));
  }

  public setOperator(operator: DynamicFilterOperator) {
    // Switching between a single-value and a set operator (e.g. "is
    // exactly" <-> "is one of") would strand an incompatible value shape
    // (a scalar vs an array) in the property -- clear it instead.
    const wasSetOperator = SET_OPERATORS.includes(this.operator);
    const isSetOperator = SET_OPERATORS.includes(operator);
    if (wasSetOperator !== isSetOperator) {
      this.property.clear();
    }

    this.operator = operator;
  }

  public get operatorLabel() {
    return this.operator ? this.operatorsMap[this.operator] : "";
  }

  public get value() {
    if (this.property.isEmpty) return undefined;

    if (this.operator && SET_OPERATORS.includes(this.operator)) {
      const values = this.property.value;
      if (!Array.isArray(values) || !values.length) return undefined;

      const options: Record<string, string> =
        (this.field as any).options || {};
      return values.map((val) => options[val] ?? val).join(", ");
    }

    return this.field.isEmpty ? undefined : this.field.stringValue;
  }

  public getValueAsync(): Promise<any> {
    return Promise.resolve(this.value);
  }

  public get isSet() {
    return (
      this.operator &&
      (VALUELESS_OPERATORS.includes(this.operator) ||
        this.value !== undefined)
    );
  }

  protected _label?: string;
  public get label() {
    if (this._label) return this._label;
    return this.field.label;
  }

  public getUiField() {
    const isSetOperator =
      !!this.operator && SET_OPERATORS.includes(this.operator);

    return this.field.clone({
      isOrphan: true,
      // A choice/select field renders a single-select unless told
      // otherwise; a set operator ("is one of" / "is none of") needs the
      // multi-select widget. Harmless on a field type that ignores it.
      ...(isSetOperator ? ({ multiple: true } as any) : {}),
    });
  }
}
